import { Box } from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { useEffect, useState } from 'react';
import { PhosphorArrowDownIcon } from '../../../../theme/icons/phosphor';

const bounce = keyframes`
  0%, 100% { transform: translateY(0); }
  40% { transform: translateY(-8px); }
  60% { transform: translateY(-4px); }
`;

const BOUNCE_ITERATIONS = 3;
const BOUNCE_DURATION_MS = 600;

interface PresetDetailScrollButtonProps {
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  isAtBottom: boolean;
  bounceTrigger: number;
}

const PresetDetailScrollButton = ({
  scrollContainerRef,
  isAtBottom,
  bounceTrigger,
}: PresetDetailScrollButtonProps) => {
  const [shouldBounce, setShouldBounce] = useState(false);

  useEffect(() => {
    if (bounceTrigger <= 0) return undefined;
    const startTimer = setTimeout(() => setShouldBounce(true), 300);
    const stopTimer = setTimeout(
      () => setShouldBounce(false),
      300 + BOUNCE_ITERATIONS * BOUNCE_DURATION_MS
    );
    return () => {
      clearTimeout(startTimer);
      clearTimeout(stopTimer);
    };
  }, [bounceTrigger]);

  const handleClick = () => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const scrollAmount = container.clientHeight * 0.75;
    const remaining =
      container.scrollHeight - container.scrollTop - container.clientHeight;
    container.scrollTo({
      top:
        remaining <= scrollAmount
          ? container.scrollHeight
          : container.scrollTop + scrollAmount,
      behavior: 'smooth',
    });
  };

  return (
    <Box
      role="button"
      aria-label="Scroll down"
      tabIndex={isAtBottom ? -1 : 0}
      cursor={isAtBottom ? 'default' : 'pointer'}
      onClick={isAtBottom ? undefined : handleClick}
      opacity={isAtBottom ? 0 : 1}
      pointerEvents={isAtBottom ? 'none' : 'auto'}
      transition="opacity 0.2s ease"
      animation={
        shouldBounce
          ? `${bounce} ${BOUNCE_DURATION_MS}ms ease-out ${BOUNCE_ITERATIONS} both`
          : undefined
      }
      w="48px"
      h="48px"
      borderRadius="full"
      bg="inkBlue"
      boxShadow="lg"
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      <PhosphorArrowDownIcon boxSize="22px" color="softWhite" />
    </Box>
  );
};

export default PresetDetailScrollButton;
